import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, finalize, firstValueFrom, fromEvent } from 'rxjs';

import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { Httpservice } from '../../../services/httpservice';
import { Commonservice } from '../../../services/commonservice';
import { ClickOutsideDirective } from '../../../helper/directives/click-outside.directive';
import { AppPreferencesService } from '../../../services/app-preferences';
import { SessionService } from '../../../services/session.service';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import {
  ManageGeneratorAutocompleteComponent,
  ManageGeneratorAutocompleteCompleteEvent,
} from '../../master/manage-generator/manage-generator-autocomplete';
import { ReportShellComponent } from '../../structure/report-shell/report-shell';
import { getTodayDateValue, formatGenerationDetailsDateLabel } from './generation-details-report.data';
import {
  GenerationDetailsBlockCell,
  GenerationDetailsBlockDefinition,
  GenerationDetailsBlockGroup,
  GenerationDetailsReportResponse,
  GenerationDetailsRow,
  GenerationDetailsSummary,
  GenerationDetailsValueFilterOption,
  GenerationDetailsViewMode,
  GenerationDetailsSubstationOption,
} from './generation-details-report.interfaces';
import { ReportDataParsingBase, stripTrailingCapacityLabel } from '../shared/report-ui.helpers';

interface GenerationDetailsHierarchyNode {
  row: GenerationDetailsRow;
  children: GenerationDetailsHierarchyNode[];
}

interface PendingRemarksContext {
  mode: 'block' | 'day';
  station_id: number;
  block_no?: number;
  block_value: number | null;
}

interface GenerationDetailsHierarchySubGroup {
  label: string;
  rows: GenerationDetailsRow[];
}

interface GenerationDetailsHierarchySourceGroup {
  label: string;
  subGroups: Map<string, GenerationDetailsHierarchySubGroup>;
  directRows: GenerationDetailsRow[];
}

interface GenerationDetailsHierarchyOwnerGroup {
  label: string;
  sourceGroups: Map<string, GenerationDetailsHierarchySourceGroup>;
  footerRows: GenerationDetailsRow[];
}

interface GenerationDetailsValueOption {
  value: GenerationDetailsValueFilterOption;
  label: string;
}

interface GenerationDetailsHierarchyGroups {
  ownerGroupMap: Map<string, GenerationDetailsHierarchyOwnerGroup>;
  ownerLabels: string[];
}

interface GenerationDetailsAggregateRowOptions {
  includeTotalInLabel?: boolean;
  includeInstalledCapacityInLabel?: boolean;
  forceExpandChildren?: boolean;
}

type GenerationDetailsDisplayMode = 'generation' | 'spill' | 'water-level' | 'voltage' | 'interruption' | 'summary';
type GenerationDetailsExcelMode = 'day' | 'blocks';

const GENERATION_DETAILS_ONLY_XLSX_MESSAGE = 'Please select a valid .xlsx file.';

interface GenerationDetailsBsCalendarCell {
  bs_date: string;
  bs_date_label: string;
  ad_date: string;
  in_current_month: boolean;
  is_today: boolean;
  is_selected: boolean;
}

interface GenerationDetailsBsCalendar {
  bs_year: number;
  bs_month: number;
  bs_month_name: string;
  cells: GenerationDetailsBsCalendarCell[];
}

function formatBsDate(value: string): string {
  const parts = value.split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : value;
}

function parseBsDate(value: string): string {
  const parts = value.split('/');
  return parts.length === 3 ? `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}` : value;
}

@Component({
  selector: 'app-generation-details-report-page',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ReportShellComponent, ManageGeneratorAutocompleteComponent, ClickOutsideDirective],
  templateUrl: './generation-details-report.html',
  styleUrl: './generation-details-report.scss',
})
export class GenerationDetailsReportPage extends ReportDataParsingBase implements OnInit {
  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly filtersForm: UntypedFormGroup;
  protected readonly valueOptions: GenerationDetailsValueOption[] = [
    { value: 'SCADA', label: 'SCADA' },
    { value: 'ENERGY_METER', label: 'Energy Meter' },
    { value: 'MANUAL', label: 'Through Source' },
    { value: 'FINAL', label: 'Final' },
  ];

  protected isLoading = false;
  protected isExporting = false;
  protected isDownloadingTemplate = false;
  protected isImporting = false;
  protected isGenerationActionMenuOpen = false;
  protected bsCalendarOpen = false;
  protected bsCalendarLoading = false;
  protected bsCalendarMonth: GenerationDetailsBsCalendar | null = null;
  protected readonly bsCalendarWeekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  protected isSavingCell = false;

  protected viewMode: GenerationDetailsViewMode = 'blocks';
  protected detailMode: GenerationDetailsDisplayMode = 'generation';
  protected showSubstationFilter = true;
  protected substationSearchTerm = '';

  protected blockColumns: GenerationDetailsBlockDefinition[] = [];
  protected blockGroups: GenerationDetailsBlockGroup[] = [];
  protected rows: GenerationDetailsRow[] = [];
  protected footerRows: GenerationDetailsRow[] = [];
  protected baseLoad = 0;
  protected summary: GenerationDetailsSummary = {
    total_mwh: 0,
    row_count: 0,
  };

  protected substationOptions: GenerationDetailsSubstationOption[] = [];
  protected filteredSubstationOptions: GenerationDetailsSubstationOption[] = [];
  protected activeCellKey: string | null = null;
  protected cellDraftValue = '';
  protected remarksModalOpen = false;
  protected remarksForm: UntypedFormGroup;

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly sessionSvc = inject(SessionService);
  private hierarchyRoots: GenerationDetailsHierarchyNode[] = [];
  private generationTotalRow: GenerationDetailsRow | null = null;
  private flatRows: GenerationDetailsRow[] = [];
  private expandedRowKeys = new Set<string>();
  private pendingRemarksContext: PendingRemarksContext | null = null;
  private isRestrictedReport = false;
  private selectedAdDate = getTodayDateValue();

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly httpSvc: Httpservice,
    private readonly commonSvc: Commonservice,
    private readonly preferencesSvc: AppPreferencesService,
  ) {
    super();
    this.filtersForm = this.fb.group({
      date: [getTodayDateValue(), Validators.required],
      substation_id: [null],
      show_values: [null],
    });
    this.remarksForm = this.fb.group({
      data_value: [''],
      copy_previous_block_value: [false],
      carry_over_previous_day_values: [false],
      remarks: [''],
    });
    this.showSubstationFilter = !this.isRestrictedGenerationUserRole(this.sessionSvc.getProfileDetails()?.role_name);
    if (!this.showSubstationFilter) {
      const userSubstationId = this.sessionSvc.getSubstationId();
      if (userSubstationId !== null) {
        this.filtersForm.patchValue({ substation_id: userSubstationId }, { emitEvent: false });
      }
    }
  }

  async ngOnInit(): Promise<void> {
    this.bindFilterListeners();
    this.listenToDatePreferenceChanges();
    this.loadSubstationOptions();
    if (this.isBsDateMode) {
      await this.convertDateControl();
    }
    this.loadReport();
  }

  protected clearFilters(): void {
    this.filtersForm.reset(
      {
        date: getTodayDateValue(),
        substation_id: this.showSubstationFilter ? null : this.sessionSvc.getSubstationId(),
        show_values: null,
      },
      { emitEvent: false },
    );
    this.substationSearchTerm = '';
    this.isGenerationActionMenuOpen = false;
    this.selectedAdDate = getTodayDateValue();
    this.refreshSubstationOptionsForDetailMode();
    if (this.isBsDateMode) {
      void this.convertDateControl().then(() => this.loadReport());
      return;
    }
    this.loadReport();
  }

  protected handleExportToExcel(): void {
    if (this.isExporting) {
      return;
    }

    this.isExporting = true;
    this.httpSvc
      .downloadfile(AuthEndPoints.EXPORT_GENERATION_DETAILS_REPORT, ApiMethod.POST, this.buildReportPayload())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isExporting = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (blob) => this.downloadBlob(blob, this.buildExportFileName()),
      });
  }

  protected toggleGenerationActionMenu(): void {
    if (this.isExcelActionBusy()) {
      return;
    }

    this.isGenerationActionMenuOpen = !this.isGenerationActionMenuOpen;
  }

  protected exportGenerationDetailsData(): void {
    this.isGenerationActionMenuOpen = false;
    this.handleExportToExcel();
  }

  protected downloadGenerationDetailsTemplate(mode: GenerationDetailsExcelMode): void {
    if (this.isExcelActionBusy()) {
      return;
    }

    this.isGenerationActionMenuOpen = false;
    this.isDownloadingTemplate = true;
    this.httpSvc
      .downloadfile(AuthEndPoints.GENERATION_DETAILS_TEMPLATE, ApiMethod.POST, this.buildExcelPayload(mode))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isDownloadingTemplate = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (blob) => this.downloadBlob(blob, this.buildTemplateFileName(mode)),
      });
  }

  protected triggerGenerationDetailsImportFilePicker(fileInput: HTMLInputElement | null): void {
    if (!fileInput || this.isExcelActionBusy()) {
      return;
    }

    this.isGenerationActionMenuOpen = false;
    fileInput.click();
  }

  protected onGenerationDetailsImportFileSelected(event: Event, mode: GenerationDetailsExcelMode): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.length ? input.files[0] : null;
    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      this.commonSvc.showErrorToastr(GENERATION_DETAILS_ONLY_XLSX_MESSAGE);
      this.resetImportInput(input);
      return;
    }

    this.isImporting = true;
    this.httpSvc
      .requestCall(AuthEndPoints.IMPORT_GENERATION_DETAILS_REPORT, ApiMethod.POST, this.buildImportFormData(file, mode), true)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isImporting = false;
          this.resetImportInput(input);
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: () => this.loadReport(),
      });
  }

  protected handleViewModeChange(mode: GenerationDetailsViewMode): void {
    if (this.viewMode === mode) {
      return;
    }

    this.viewMode = mode;
    this.isGenerationActionMenuOpen = false;
    this.cancelCellEdit();
    this.closeRemarksModal();
    this.loadReport();
  }

  protected handleDetailModeChange(mode: GenerationDetailsDisplayMode): void {
    if (this.detailMode === mode) {
      return;
    }

    this.detailMode = mode;
    this.isGenerationActionMenuOpen = false;
    this.expandedRowKeys = this.getDefaultExpandedRowKeys(this.hierarchyRoots);
    this.refreshSubstationOptionsForDetailMode();
    this.rows = this.buildVisibleRows();
    this.loadReport();
  }

  protected isDetailModeActive(mode: GenerationDetailsDisplayMode): boolean {
    return this.detailMode === mode; 
  }

  protected shouldShowRestrictedWaterLevelTab(): boolean {
    return this.isRestrictedReport || this.isRestrictedGenerationUserRole(this.sessionSvc.getProfileDetails()?.role_name);
  }

  protected isSummaryMode(): boolean {
    return this.detailMode === 'summary';
  }

  protected trackByBlock(_: number, item: GenerationDetailsBlockDefinition): number {
    return item.block_no;
  }

  protected trackByBlockGroup(_: number, item: GenerationDetailsBlockGroup): string {
    return `${item.period}-${item.span}`;
  }

  protected trackByRow(_: number, item: GenerationDetailsRow): string {
    return item.row_key ?? `${item.row_kind ?? 'data'}-${item.station_id}`;
  }

  protected trackBySubstation(_: number, item: GenerationDetailsSubstationOption): number {
    return item.id;
  }

  protected onSubstationSearchInput(event: ManageGeneratorAutocompleteCompleteEvent): void {
    const query = event?.query ?? '';
    this.filterSubstationAutocomplete(query);

    const normalizedQuery = typeof query === 'string' ? query.trim() : '';
    if (!normalizedQuery) {
      this.resetSubstationSelection(true);
      return;
    }

    const match = this.findSubstationByLabel(normalizedQuery);
    if (match) {
      this.setSubstationSelection(match.id, true);
    }
  }

  protected onSubstationSearchBlur(): void {
    this.commitSubstationAutocompleteValue();
  }

  protected getFilterDateLabel(): string {
    return formatGenerationDetailsDateLabel(this.getSelectedDateValue());
  }

  protected getHeaderSummary(): string {
    const count = this.summary.row_count;
    const blockCount = this.blockColumns.length;
    if (this.viewMode === 'day') {
      return `Showing ${count} generator${count === 1 ? '' : 's'} • day total view`;
    }
    return `Showing ${count} generator${count === 1 ? '' : 's'} • ${blockCount} block${blockCount === 1 ? '' : 's'}`;
  }

  protected getCellText(cell: GenerationDetailsBlockCell): string {
    return cell.value === null || cell.value === undefined ? '_' : this.formatNumber(cell.value);
  }

  protected getDayTotalText(row: GenerationDetailsRow): string {
    return this.formatNumber(row.day_total_mwh);
  }

  protected getValueModeLabel(): string {
    return this.normalizeSelectedValueMode() === 'MW' ? 'MW' : 'MWh';
  }

  protected getValueOptionLabel(value: GenerationDetailsValueFilterOption): string {
    return this.valueOptions.find((option) => option.value === value)?.label ?? value;
  }

  protected getDayRemarks(row: GenerationDetailsRow): string | null {
    return row.blocks.find((cell) => typeof cell.remarks === 'string' && cell.remarks.trim())?.remarks ?? null;
  }

  protected getRowMaxText(row: GenerationDetailsRow): string {
    return this.formatNumber(this.getRowBlockMaxValue(row) ?? 0);
  }

  protected getRowGenerationText(row: GenerationDetailsRow): string {
    return this.formatNumber(this.calculateRowTotal(row.blocks));
  }

  protected isHierarchyTotalRow(row: GenerationDetailsRow): boolean {
    if (this.isWaterLevelDetailMode() && row.row_kind === 'power-water-level-generator') {
      return false;
    }

    if (this.isEditableGenerationRow(row)) {
      return false;
    }

    return row.row_kind !== 'data';
  }

  protected hasHierarchyChildren(row: GenerationDetailsRow): boolean {
    return Boolean(row.has_children && row.row_key);
  }

  protected isHierarchyExpanded(row: GenerationDetailsRow): boolean {
    return Boolean(row.row_key && this.expandedRowKeys.has(row.row_key));
  }

  protected toggleHierarchyRow(row: GenerationDetailsRow): void {
    if (!row.row_key || !row.has_children) {
      return;
    }

    if (this.expandedRowKeys.has(row.row_key)) {
      this.expandedRowKeys.delete(row.row_key);
    } else {
      this.expandedRowKeys.add(row.row_key);
    }

    this.rows = this.detailMode === 'interruption'
      ? this.flattenHierarchyNodes(this.hierarchyRoots, this.expandedRowKeys)
      : this.buildVisibleRows();
  }

  protected isCellInteractive(row: GenerationDetailsRow, cell: GenerationDetailsBlockCell): boolean {
    return (
      this.viewMode === 'blocks' &&
      this.isEditableGenerationRow(row) &&
      this.isCurrentDateSelected() &&
      !this.isLoading &&
      !this.isExporting &&
      !this.isSavingCell &&
      !this.remarksModalOpen
    );
  }

  protected isEditedCell(cell: GenerationDetailsBlockCell): boolean {
    return cell.source === 'logsheet_data_details';
  }

  protected isAdminEditedCell(cell: GenerationDetailsBlockCell): boolean {
    return this.isEditedCell(cell) && cell.editor_role_group === 'admin';
  }

  protected isUserEditedCell(cell: GenerationDetailsBlockCell): boolean {
    return this.isEditedCell(cell) && cell.editor_role_group !== 'admin';
  }

  protected isDayTotalInteractive(row: GenerationDetailsRow): boolean {
    return (
      this.viewMode === 'day' &&
      this.isEditableGenerationRow(row) &&
      this.isCurrentDateSelected() &&
      !this.isLoading &&
      !this.isExporting &&
      !this.isSavingCell &&
      !this.remarksModalOpen
    );
  }

  private isEditableGenerationRow(row: GenerationDetailsRow): boolean {
    const rowKind = row.row_kind ?? 'data';
    return (
      rowKind === 'data' ||
      rowKind === 'power-thermal-generator' ||
      rowKind === 'power-kabeli-corridor-generator'
    );
  }

  protected isEditingCell(stationId: number, blockNo: number): boolean {
    return this.activeCellKey === this.createCellKey(stationId, blockNo);
  }

  protected isEditingDayTotal(row: GenerationDetailsRow): boolean {
    return this.activeCellKey === this.createDayTotalKey(row.station_id);
  }

  protected beginCellEdit(row: GenerationDetailsRow, cell: GenerationDetailsBlockCell): void {
    if (!this.isCellInteractive(row, cell)) {
      return;
    }

    this.activeCellKey = this.createCellKey(row.station_id, cell.block_no);
    this.cellDraftValue = cell.value === null || cell.value === undefined ? '' : String(cell.value);
  }

  protected beginDayTotalEdit(row: GenerationDetailsRow): void {
    if (!this.isDayTotalInteractive(row)) {
      return;
    }

    this.activeCellKey = this.createDayTotalKey(row.station_id);
    this.cellDraftValue = row.day_total_mwh === null || row.day_total_mwh === undefined ? '' : String(row.day_total_mwh);
  }

  protected handleCellInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.cellDraftValue = target?.value ?? '';
  }

  protected handleCellBlur(row: GenerationDetailsRow, cell: GenerationDetailsBlockCell): void {
    this.commitCellEdit(row, cell);
  }

  protected handleDayTotalBlur(row: GenerationDetailsRow): void {
    this.commitDayTotalEdit(row);
  }

  protected handleCellKeydown(event: KeyboardEvent, row: GenerationDetailsRow, cell: GenerationDetailsBlockCell): void {
    this.handleEditKeydown(event, () => this.commitCellEdit(row, cell));
  }

  protected handleDayTotalKeydown(event: KeyboardEvent, row: GenerationDetailsRow): void {
    this.handleEditKeydown(event, () => this.commitDayTotalEdit(row));
  }

  private handleEditKeydown(event: KeyboardEvent, commitAction: () => void): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitAction();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelCellEdit();
    }
  }

  protected cancelCellEdit(): void {
    this.resetCellEditor();
  }

  protected closeRemarksModal(): void {
    this.remarksModalOpen = false;
    this.pendingRemarksContext = null;
    this.resetRemarksForm();
  }

  protected saveRemarks(): void {
    if (!this.pendingRemarksContext) {
      this.closeRemarksModal();
      return;
    }

    const context = { ...this.pendingRemarksContext };
    const formValue = this.remarksForm.getRawValue();
    const remarksControl = this.remarksForm.get('remarks');
    const remarks = typeof remarksControl?.value === 'string' ? remarksControl.value.trim() : '';
    const dataValue = this.normalizeNullableNumber(formValue.data_value);
    const endpoint = context.mode === 'day' ? AuthEndPoints.UPDATE_GENERATION_DETAILS_DAY_TOTAL : AuthEndPoints.UPDATE_GENERATION_DETAILS_CELL;
    const payload = context.mode === 'day'
      ? {
          data_date: this.getSelectedDateValue(),
          generator_id: context.station_id,
          data_value: dataValue,
          remarks: remarks || null,
          detail_mode: this.detailMode,
        }
      : {
          data_date: this.getSelectedDateValue(),
          generator_id: context.station_id,
          block_no: context.block_no,
          block_value: dataValue,
          value_unit: 'MWH',
          remarks: remarks || null,
          detail_mode: this.detailMode,
        };

    const onSuccess = (): void => {
      this.closeRemarksModal();
      this.loadReport();
    };

    this.isSavingCell = true;
    this.httpSvc
      .requestCall(endpoint, ApiMethod.PATCH, payload, true)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isSavingCell = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: onSuccess,
      });
  }

  protected onEditDataValueInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    const nextValue = target?.value ?? '';
    if (nextValue.trim()) {
      this.resetPreviousValueToggles('copy_previous_block_value', 'carry_over_previous_day_values');
    }
  }

  private resetPreviousValueToggles(...controlNames: string[]): void {
    controlNames
      .map((controlName) => this.remarksForm.get(controlName))
      .filter((control) => Boolean(control?.value))
      .forEach((control) => control?.patchValue(false, { emitEvent: false }));
  }

  protected arePreviousValueControlsDisabled(): boolean {
    const dataValue = this.remarksForm.get('data_value')?.value;
    return typeof dataValue === 'string' && dataValue.trim().length > 0;
  }

  protected onCopyPreviousBlockValueChange(): void {
    const copyControl = this.remarksForm.get('copy_previous_block_value');
    if (!copyControl?.value || this.pendingRemarksContext?.block_no === undefined) {
      return;
    }

    this.remarksForm.patchValue({ carry_over_previous_day_values: false }, { emitEvent: false });
    this.fetchPreviousGenerationValue(
      AuthEndPoints.GET_GENERATION_DETAILS_PREVIOUS_BLOCK_VALUE,
      this.pendingRemarksContext.station_id,
      this.pendingRemarksContext.block_no,
    );
  }

  protected onCarryOverPreviousDayValuesChange(): void {
    const carryControl = this.remarksForm.get('carry_over_previous_day_values');
    if (!carryControl?.value || this.pendingRemarksContext?.block_no === undefined) {
      return;
    }

    this.remarksForm.patchValue({ copy_previous_block_value: false }, { emitEvent: false });
    this.fetchPreviousGenerationValue(
      AuthEndPoints.GET_GENERATION_DETAILS_PREVIOUS_DAY_BLOCK_VALUE,
      this.pendingRemarksContext.station_id,
      this.pendingRemarksContext.block_no,
    );
  }

  protected get isBsDateMode(): boolean {
    return this.preferencesSvc.showBsDate;
  }

  protected getDateInputType(): 'date' | 'text' {
    return this.isBsDateMode ? 'text' : 'date';
  }

  protected openBsDatePicker(): void {
    if (!this.isBsDateMode) {
      return;
    }
    if (this.bsCalendarOpen) {
      this.bsCalendarOpen = false;
      return;
    }
    void this.loadBsCalendar(this.getDateControlValue(), 'BS');
  }

  protected closeBsDatePicker(): void {
    this.bsCalendarOpen = false;
  }

  protected selectBsCalendarCell(cell: GenerationDetailsBsCalendarCell): void {
    if (!cell.bs_date) {
      return;
    }
    this.closeBsDatePicker();
    this.selectedAdDate = cell.ad_date;
    this.filtersForm.patchValue({ date: formatBsDate(cell.bs_date) });
  }

  protected shiftBsCalendarMonth(offset: number): void {
    if (!this.bsCalendarMonth || !offset) {
      return;
    }
    let month = this.bsCalendarMonth.bs_month + offset;
    let year = this.bsCalendarMonth.bs_year;
    if (month < 1) {
      month = 12;
      year -= 1;
    }
    if (month > 12) {
      month = 1;
      year += 1;
    }
    void this.loadBsCalendar(`${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-01`, 'BS');
  }

  protected formatNumber(value: number | null): string {
    if (value === null || value === undefined || !Number.isFinite(Number(value))) {
      return '-';
    }
    return Number(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  protected getBlocksColspan(): number {
    return this.blockColumns.length + 2;
  }

  protected getDayColspan(): number {
    return 2;
  }

  protected getPeriodTitle(period: GenerationDetailsBlockDefinition['period']): string {
    switch (period) {
      case 'morning':
        return 'Morning';
      case 'day':
        return 'Day';
      case 'night':
        return 'Night';
      default:
        return 'Blocks';
    }
  }

  protected getBlockGroupClass(period: GenerationDetailsBlockDefinition['period']): string {
    return `generation-details-table__band--${period}`;
  }

  private listenToDatePreferenceChanges(): void {
    if (typeof window === 'undefined') {
      return;
    }
    fromEvent<CustomEvent<{ name?: string }>>(window, 'app-preferences-change')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.detail?.name !== 'showBsDate') {
          return;
        }
        this.closeBsDatePicker();
        void this.convertDateControl().then(() => this.loadReport());
      });
  }

  private async convertDateControl(): Promise<void> {
    const targetMode = this.isBsDateMode ? 'BS' : 'AD';
    const sourceMode = targetMode === 'BS' ? 'AD' : 'BS';
    const value = this.getDateControlValue();
    if (!value) {
      return;
    }
    try {
      const response = await firstValueFrom(this.httpSvc.requestCall(
        AuthEndPoints.CONVERT_LOG_SHEET_DATE,
        ApiMethod.POST,
        {
          data_date: sourceMode === 'BS' ? parseBsDate(value) : value,
          bs_ad_key: targetMode,
          source_bs_ad_key: sourceMode,
        },
        false,
        { suppressToast: true },
      ));
      const result = response?.value;
      if (typeof result?.data_date === 'string' && result.data_date) {
        this.selectedAdDate = result.data_date;
      }
      const nextValue = targetMode === 'BS' ? result?.bs_date_label : result?.data_date;
      if (typeof nextValue === 'string' && nextValue) {
        this.filtersForm.patchValue({ date: nextValue }, { emitEvent: false });
      }
    } catch {
      // Keep the current date when a preference conversion is unavailable.
    }
  }

  protected getDateControlValue(): string {
    const value = this.filtersForm.get('date')?.value;
    return typeof value === 'string' ? value : '';
  }

  private toApiDateValue(value: string): string {
    return this.isBsDateMode ? this.selectedAdDate : value;
  }

  private async loadBsCalendar(dateValue: string, bsAdKey: 'AD' | 'BS'): Promise<void> {
    if (!dateValue) {
      return;
    }
    this.bsCalendarOpen = true;
    this.bsCalendarLoading = true;
    try {
      const response = await firstValueFrom(this.httpSvc.requestCall(
        AuthEndPoints.GET_LOG_SHEET_BS_CALENDAR,
        ApiMethod.POST,
        { data_date: bsAdKey === 'BS' ? parseBsDate(dateValue) : dateValue, bs_ad_key: bsAdKey },
        false,
        { suppressToast: true },
      ));
      const value = response?.value ?? {};
      this.bsCalendarMonth = {
        bs_year: Number(value.bs_year) || 0,
        bs_month: Number(value.bs_month) || 0,
        bs_month_name: value.bs_month_name || '',
        cells: Array.isArray(value.cells) ? value.cells : [],
      };
    } catch {
      this.bsCalendarMonth = null;
    } finally {
      this.bsCalendarLoading = false;
      this.cdr.detectChanges();
    }
  }

  private bindFilterListeners(): void {
    this.filtersForm.get('date')?.valueChanges.pipe(debounceTime(150), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.isBsDateMode) {
        this.selectedAdDate = this.getDateControlValue();
      }
      this.loadReport();
    });
    this.filtersForm.get('substation_id')?.valueChanges.pipe(debounceTime(150), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.loadReport());
    this.filtersForm.get('show_values')?.valueChanges.pipe(debounceTime(150), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.loadReport());
    this.remarksForm.get('copy_previous_block_value')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onCopyPreviousBlockValueChange());
    this.remarksForm.get('carry_over_previous_day_values')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onCarryOverPreviousDayValuesChange());
  }

  private loadSubstationOptions(): void {
    if (!this.showSubstationFilter) {
      this.substationOptions = [];
      this.filteredSubstationOptions = [];
      return;
    }

    this.httpSvc
      .requestCall(AuthEndPoints.GET_GENERATORS_LIST, ApiMethod.GET, '', false, { suppressToast: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.substationOptions = this.extractSubstationOptions(response);
          this.refreshSubstationOptionsForDetailMode();
          this.cdr.detectChanges();
        },
        error: () => {
          this.substationOptions = [];
          this.refreshSubstationOptionsForDetailMode();
          this.cdr.detectChanges();
        },
      });
  }

  private loadReport(): void {
    this.isLoading = true;
    this.httpSvc
      .requestCall(AuthEndPoints.GET_GENERATION_DETAILS_REPORT, ApiMethod.POST, this.buildReportPayload(), false, { suppressToast: true })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => this.applyReport(this.extractReport(response)),
        error: () => this.applyReport(null),
      });
  }

  private applyReport(report: GenerationDetailsReportResponse | null): void {
    this.cancelCellEdit();
    this.closeRemarksModal();

    if (!report) {
      this.resetReportState();
      return;
    }

    this.blockColumns = report.blocks;
    this.blockGroups = this.buildBlockGroups(report.blocks);
    this.isRestrictedReport = Boolean(report.restricted_user_mode);
    if (this.isDirectRowsDetailMode() || this.isRestrictedReport) {
      this.applyDirectReportRows(report.rows);
    } else {
      this.applyHierarchicalReportRows(report.rows);
    }
    this.summary = report.summary;
    this.baseLoad = report.base_load ?? 0;
    this.syncReportSubstationFilter(report);
  }

  private resetReportState(): void {
    this.blockColumns = [];
    this.blockGroups = [];
    this.rows = [];
    this.footerRows = [];
    this.hierarchyRoots = [];
    this.generationTotalRow = null;
    this.flatRows = [];
    this.expandedRowKeys.clear();
    this.baseLoad = 0;
    this.isRestrictedReport = false;
    this.summary = { total_mwh: 0, row_count: 0 };
  }

  private applyDirectReportRows(rows: GenerationDetailsRow[]): void {
    this.generationTotalRow = null;
    this.footerRows = [];
    this.flatRows = [];
    if (this.isWaterLevelDetailMode()) {
      this.hierarchyRoots = [];
      this.expandedRowKeys.clear();
      this.rows = this.getWaterLevelGeneratorRows(rows);
      return;
    }

    this.hierarchyRoots = this.detailMode === 'interruption' ? this.buildLinkedRowNodes(rows) : [];
    this.expandedRowKeys = this.detailMode === 'interruption' ? this.getDefaultFlatExpandedRowKeys(this.hierarchyRoots) : new Set<string>();
    this.rows = this.detailMode === 'interruption'
      ? this.flattenHierarchyNodes(this.hierarchyRoots, this.expandedRowKeys)
      : rows;
  }

  private getWaterLevelGeneratorRows(rows: GenerationDetailsRow[]): GenerationDetailsRow[] {
    return rows.filter((row) => row.row_kind === 'data' || row.row_kind === 'power-water-level-generator');
  }

  private applyHierarchicalReportRows(rows: GenerationDetailsRow[]): void {
    const hierarchyRows = rows.filter((row) => !row.force_flat || this.isPowerNestedRow(row));
    const flatRows = rows.filter((row) => row.force_flat && !this.isPowerNestedRow(row));
    const totalRows = [
      ...hierarchyRows.filter((row) => !this.isPowerNestedRow(row)),
      ...rows.filter((row) => row.row_key === 'power-total-thermal' || row.row_kind === 'power-kabeli-corridor'),
    ];
    this.hierarchyRoots = this.buildHierarchyNodes(hierarchyRows);
    this.generationTotalRow = totalRows.length ? this.buildTotalGenerationRow(totalRows) : null;
    this.flatRows = flatRows;
    this.footerRows = this.buildFooterRows(rows);
    this.expandedRowKeys = this.getDefaultExpandedRowKeys(this.hierarchyRoots);
    this.addDefaultFlatExpandedRowKeys(this.buildLinkedRowNodes(this.flatRows), this.expandedRowKeys);
    this.rows = this.buildVisibleRows();
  }

  private syncReportSubstationFilter(report: GenerationDetailsReportResponse): void {
    if (report.filters?.substation_id !== null && report.filters?.substation_id !== undefined) {
      this.filtersForm.patchValue({ substation_id: report.filters.substation_id }, { emitEvent: false });
      if (this.showSubstationFilter) {
        this.syncSubstationAutocompleteValue(report.filters.substation_id);
      }
    } else if (this.showSubstationFilter) {
      this.substationSearchTerm = '';
    }
  }

  private commitCellEdit(row: GenerationDetailsRow, cell: GenerationDetailsBlockCell): void {
    if (!this.isEditingCell(row.station_id, cell.block_no)) {
      return;
    }

    const nextValue = this.normalizeCellValue(this.cellDraftValue);
    const currentValue = cell.value === null || cell.value === undefined ? null : Number(cell.value);
    if (nextValue === currentValue) {
      this.cancelCellEdit();
      return;
    }

    this.cancelCellEdit();
    this.openRemarksModal(
      {
        mode: 'block',
        station_id: row.station_id,
        block_no: cell.block_no,
        block_value: nextValue,
      },
      cell.remarks ?? null,
    );
  }

  private commitDayTotalEdit(row: GenerationDetailsRow): void {
    if (!this.isEditingDayTotal(row)) {
      return;
    }

    const nextValue = this.normalizeCellValue(this.cellDraftValue);
    const currentValue = row.day_total_mwh === null || row.day_total_mwh === undefined ? null : Number(row.day_total_mwh);
    if (nextValue === currentValue) {
      this.cancelCellEdit();
      return;
    }

    this.cancelCellEdit();
    this.openRemarksModal(
      {
        mode: 'day',
        station_id: row.station_id,
        block_value: nextValue,
      },
      this.getDayRemarks(row),
    );
  }

  private openRemarksModal(context: PendingRemarksContext, remarks: string | null): void {
    this.pendingRemarksContext = context;
    this.remarksForm.reset(
      {
        data_value: context.block_value === null || context.block_value === undefined ? '' : String(context.block_value),
        copy_previous_block_value: false,
        carry_over_previous_day_values: false,
        remarks: remarks ?? '',
      },
      { emitEvent: false },
    );
    this.remarksModalOpen = true;
  }

  private fetchPreviousGenerationValue(endpoint: string, stationId: number, blockNo: number): void {
    const payload = {
      data_date: this.getSelectedDateValue(),
      generator_id: stationId,
      block_no: blockNo,
      detail_mode: this.detailMode,
    };

    this.httpSvc
      .requestCall(endpoint, ApiMethod.POST, payload, false, { suppressToast: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => this.patchPreviousGenerationValue(response),
        error: () => {
          const controlKey = endpoint === AuthEndPoints.GET_GENERATION_DETAILS_PREVIOUS_BLOCK_VALUE
            ? 'copy_previous_block_value'
            : 'carry_over_previous_day_values';
          this.remarksForm.patchValue({ [controlKey]: false }, { emitEvent: false });
        },
      });
  }

  private patchPreviousGenerationValue(response: unknown): void {
    const previous = this.extractPreviousBlockValue(response);
    const patch: { data_value?: string; remarks?: string } = {};
    if (previous?.data_value !== null && previous?.data_value !== undefined) {
      patch.data_value = String(previous.data_value);
    }
    if (previous?.remarks !== null && previous?.remarks !== undefined) {
      patch.remarks = previous.remarks;
    }
    if (Object.keys(patch).length) {
      this.remarksForm.patchValue(patch, { emitEvent: false });
    }
  }

  private buildReportPayload(): Record<string, unknown> {
    return {
      data_date: this.getSelectedDateValue(),
      generation_id: null,
      substation_id: this.normalizeNumber(this.filtersForm.get('substation_id')?.value),
      show_values: this.normalizeSelectedShowValues(),
      view_mode: this.viewMode,
      detail_mode: this.detailMode,
    };
  }

  private buildExcelPayload(mode: GenerationDetailsExcelMode): Record<string, unknown> {
    return {
      ...this.buildReportPayload(),
      view_mode: mode,
    };
  }

  private buildImportFormData(file: File, mode: GenerationDetailsExcelMode): FormData {
    const payload = this.buildExcelPayload(mode);
    const formData = new FormData();
    formData.append('file', file);
    Object.entries(payload).forEach(([key, value]) => {
      if (this.isFormDataScalar(value)) {
        formData.append(key, String(value));
      }
    });
    return formData;
  }

  private isFormDataScalar(value: unknown): value is string | number | boolean {
    return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
  }

  private isExcelActionBusy(): boolean {
    return this.isLoading || this.isExporting || this.isDownloadingTemplate || this.isImporting;
  }

  private isCurrentDateSelected(): boolean {
    return this.getSelectedDateValue() === getTodayDateValue();
  }

  private buildHierarchyNodes(rows: GenerationDetailsRow[]): GenerationDetailsHierarchyNode[] {
    const normalizedRows = this.normalizeGenerationRows(rows);
    const specialRows = normalizedRows.filter((row) => this.isPowerNestedRow(row));
    const { ownerGroupMap, ownerLabels } = this.groupHierarchyRows(
      normalizedRows.filter((row) => !this.isPowerNestedRow(row)),
    );

    const visibleOwnerLabels = ownerLabels.filter((ownerLabel) => ownerLabel !== 'Unassigned');
    const hierarchyNodes = visibleOwnerLabels
      .map((ownerLabel) => {
        const ownerGroup = ownerGroupMap.get(ownerLabel);
        return ownerGroup ? this.buildHierarchyOwnerNode(ownerLabel, ownerGroup) : null;
      })
      .filter((node): node is GenerationDetailsHierarchyNode => Boolean(node));

    return this.attachSpecialPowerRows(hierarchyNodes, specialRows);
  }

  private attachSpecialPowerRows(
    nodes: GenerationDetailsHierarchyNode[],
    specialRows: GenerationDetailsRow[],
  ): GenerationDetailsHierarchyNode[] {
    const smallRows = specialRows.filter((row) => row.row_kind === 'power-small');
    const thermalRow = specialRows.find((row) => row.row_key === 'power-total-thermal');
    const thermalChildren = specialRows.filter((row) => row.row_kind === 'power-thermal-generator');

    for (const row of smallRows) {
      const ownerNode = this.findOwnerNode(nodes, row.owner);
      const hydroNode = ownerNode?.children.find(
        (node) => node.row.row_kind === 'source-total' && node.row.station_name.toLowerCase().startsWith('total hydro'),
      );
      if (!hydroNode) {
        continue;
      }

      const smallCapacity = row.installed_capacity ?? 0;
      hydroNode.row.installed_capacity = Number(
        ((hydroNode.row.installed_capacity ?? 0) + smallCapacity).toFixed(5),
      );
      this.updateInstalledCapacityLabel(hydroNode.row);
      if (ownerNode) {
        ownerNode.row.installed_capacity = Number(
          ((ownerNode.row.installed_capacity ?? 0) + smallCapacity).toFixed(5),
        );
        this.updateInstalledCapacityLabel(ownerNode.row);
      }

      hydroNode.row.has_children = true;
      const lastFuelRowIndex = hydroNode.children.reduce(
        (lastIndex, child, index) => child.row.row_kind === 'subcat-total' ? index : lastIndex,
        -1,
      );
      hydroNode.children.splice(lastFuelRowIndex + 1, 0, { row: { ...row, row_level: 3 }, children: [] });
    }

    if (thermalRow) {
      const neaNode = this.findOwnerNode(nodes, 'NEA');
      if (neaNode) {
        neaNode.row.installed_capacity = Number(
          ((neaNode.row.installed_capacity ?? 0) + (thermalRow.installed_capacity ?? 0)).toFixed(5),
        );
        this.updateInstalledCapacityLabel(neaNode.row);
        const thermalDisplayRow = {
          ...thermalRow,
          station_name: `${thermalRow.station_name} (${this.formatNumber(thermalRow.installed_capacity ?? 0)})`,
          row_level: 2,
          has_children: thermalChildren.length > 0,
        };
        const solarIndex = neaNode.children.findIndex(
          (node) => node.row.row_kind === 'source-total' && node.row.station_name.toLowerCase().startsWith('total solar'),
        );
        neaNode.children.splice(solarIndex < 0 ? neaNode.children.length : solarIndex + 1, 0, {
          row: thermalDisplayRow,
          children: thermalChildren.map((row) => ({ row: { ...row, row_level: 3 }, children: [] })),
        });
      }
    }

    return nodes;
  }

  private updateInstalledCapacityLabel(row: GenerationDetailsRow): void {
    const label = stripTrailingCapacityLabel(row.station_name);
    row.station_name = `${label} (${this.formatNumber(row.installed_capacity ?? 0)})`;
  }

  private findOwnerNode(nodes: GenerationDetailsHierarchyNode[], owner: string | null | undefined): GenerationDetailsHierarchyNode | null {
    const target = (owner ?? '').trim().toLowerCase();
    return nodes.find((node) => stripTrailingCapacityLabel(node.row.station_name).trim().toLowerCase() === `total ${target}`) ?? null;
  }

  private isPowerNestedRow(row: GenerationDetailsRow): boolean {
    return row.row_kind === 'power-small'
      || row.row_kind === 'power-thermal-generator'
      || row.row_key === 'power-total-thermal';
  }

  protected isHydroSmallPowerRow(row: GenerationDetailsRow): boolean {
    return Boolean(row.is_small_power) && row.source_category?.trim().toLowerCase() === 'hydro';
  }

  private groupHierarchyRows(rows: GenerationDetailsRow[]): GenerationDetailsHierarchyGroups {
    const ownerGroupMap = new Map<string, GenerationDetailsHierarchyOwnerGroup>();
    const ownerLabels: string[] = [];

    for (const row of rows) {
      const ownerLabel = this.normalizeHierarchyLabel(row.owner);
      const ownerGroup = this.getOrCreateHierarchyOwnerGroup(ownerGroupMap, ownerLabels, ownerLabel);
      if (row.row_kind === 'power-small') {
        ownerGroup.footerRows.push(row);
        continue;
      }

      const sourceLabel = this.normalizeHierarchyLabel(row.source_category);
      const subLabel = this.normalizeOptionalHierarchyLabel(row.sub_category);
      const sourceGroup = this.getOrCreateHierarchySourceGroup(ownerGroup, sourceLabel);

      if (!subLabel) {
        sourceGroup.directRows.push(row);
        continue;
      }

      this.appendHierarchySubRow(sourceGroup, subLabel, row);
    }

    return { ownerGroupMap, ownerLabels };
  }

  private getOrCreateHierarchyOwnerGroup(
    ownerGroupMap: Map<string, GenerationDetailsHierarchyOwnerGroup>,
    ownerLabels: string[],
    ownerLabel: string,
  ): GenerationDetailsHierarchyOwnerGroup {
    let ownerGroup = ownerGroupMap.get(ownerLabel);
    if (!ownerGroup) {
      ownerGroup = {
        label: ownerLabel,
        sourceGroups: new Map<string, GenerationDetailsHierarchySourceGroup>(),
        footerRows: [],
      };
      ownerGroupMap.set(ownerLabel, ownerGroup);
      ownerLabels.push(ownerLabel);
    }
    return ownerGroup;
  }

  private getOrCreateHierarchySourceGroup(
    ownerGroup: GenerationDetailsHierarchyOwnerGroup,
    sourceLabel: string,
  ): GenerationDetailsHierarchySourceGroup {
    let sourceGroup = ownerGroup.sourceGroups.get(sourceLabel);
    if (!sourceGroup) {
      sourceGroup = {
        label: sourceLabel,
        subGroups: new Map<string, GenerationDetailsHierarchySubGroup>(),
        directRows: [],
      };
      ownerGroup.sourceGroups.set(sourceLabel, sourceGroup);
    }
    return sourceGroup;
  }

  private appendHierarchySubRow(
    sourceGroup: GenerationDetailsHierarchySourceGroup,
    subLabel: string,
    row: GenerationDetailsRow,
  ): void {
    let subGroup = sourceGroup.subGroups.get(subLabel);
    if (!subGroup) {
      subGroup = { label: subLabel, rows: [] };
      sourceGroup.subGroups.set(subLabel, subGroup);
    }
    subGroup.rows.push(row);
  }

  private buildTotalGenerationRow(rows: GenerationDetailsRow[]): GenerationDetailsRow {
    return this.buildAggregateRow(
      'generation-total',
      'Total Generation',
      this.normalizeGenerationRows(rows),
      0,
      'generation-total',
      false,
    );
  }

  private buildVisibleRows(): GenerationDetailsRow[] {
    const visibleRows = this.flattenHierarchyNodes(this.hierarchyRoots, this.expandedRowKeys);
    if (this.generationTotalRow) {
      visibleRows.push(this.generationTotalRow);
    }
    visibleRows.push(...this.flattenHierarchyNodes(this.buildLinkedRowNodes(this.flatRows), this.expandedRowKeys));
    if (this.detailMode === 'summary') {
      visibleRows.push(...this.footerRows);
    }
    return visibleRows;
  }

  private buildLinkedRowNodes(rows: GenerationDetailsRow[]): GenerationDetailsHierarchyNode[] {
    const nodeMap = new Map<string, GenerationDetailsHierarchyNode>();
    const roots: GenerationDetailsHierarchyNode[] = [];

    for (const row of rows) {
      if (row.row_key) {
        nodeMap.set(row.row_key, { row, children: [] });
      }
    }

    for (const row of rows) {
      const node = row.row_key ? nodeMap.get(row.row_key) : null;
      if (!node) {
        roots.push({ row, children: [] });
        continue;
      }

      const parentNode = row.parent_row_key ? nodeMap.get(row.parent_row_key) : null;
      if (parentNode) {
        parentNode.children.push(node);
      } else {
        roots.push(node);
      }
    }

    return roots;
  }

  private buildHierarchyOwnerNode(ownerLabel: string, ownerGroup: GenerationDetailsHierarchyOwnerGroup): GenerationDetailsHierarchyNode {
    const sourceNodes: GenerationDetailsHierarchyNode[] = [];
    const ownerRows = Array.from(ownerGroup.sourceGroups.values()).flatMap((group) => [
      ...group.directRows,
      ...Array.from(group.subGroups.values()).flatMap((subGroup) => subGroup.rows),
    ]);
    const footerNodes = ownerGroup.footerRows.map((row) => ({ row, children: [] }));

    for (const [sourceLabel, sourceGroup] of ownerGroup.sourceGroups.entries()) {
      if (sourceLabel === 'Unassigned') {
        continue;
      }
      sourceNodes.push(this.buildHierarchySourceNode(ownerLabel, sourceLabel, sourceGroup));
    }

    return {
      row: this.buildAggregateRow(
        `owner-total-${this.slugify(ownerLabel)}`,
        `Total ${ownerLabel}`,
        ownerRows,
        1,
        'owner-total',
        true,
      ),
      children: [...sourceNodes, ...footerNodes],
    };
  }

  private buildHierarchySourceNode(
    ownerLabel: string,
    sourceLabel: string,
    sourceGroup: GenerationDetailsHierarchySourceGroup,
  ): GenerationDetailsHierarchyNode {
    const allSubGroups = Array.from(sourceGroup.subGroups.entries());
    const fuelGroups = allSubGroups.filter(([, group]) => !this.isMissingHierarchyLabel(group.label));
    const missingFuelRows = allSubGroups
      .filter(([, group]) => this.isMissingHierarchyLabel(group.label))
      .flatMap(([, group]) => group.rows);
    const hasFuelGroups = fuelGroups.length > 0;
    const sourceRows = [...sourceGroup.directRows, ...allSubGroups.flatMap(([, group]) => group.rows)];
    const subNodes = fuelGroups.map(([subLabel, subGroup]) =>
      this.buildHierarchySubNode(ownerLabel, sourceLabel, subLabel, subGroup),
    );
    const directLeafNodes = [...sourceGroup.directRows, ...missingFuelRows].map((leafRow) => ({ row: leafRow, children: [] }));
    const hasChildren = this.detailMode === 'summary' ? hasFuelGroups : hasFuelGroups || directLeafNodes.length > 0;
    const includeInstalledCapacity = ['hydro', 'solar', 'bagasse', 'thermal'].includes(sourceLabel.trim().toLowerCase());

    return {
      row: this.buildAggregateRow(
        `source-total-${this.slugify(ownerLabel)}-${this.slugify(sourceLabel)}`,
        `Total ${sourceLabel}`,
        sourceRows,
        2,
        'source-total',
        hasChildren,
        {
          includeTotalInLabel: includeInstalledCapacity || (this.detailMode === 'summary' && hasFuelGroups),
          includeInstalledCapacityInLabel: includeInstalledCapacity,
        },
      ),
      children: this.getHierarchySourceChildren(hasFuelGroups, subNodes, directLeafNodes),
    };
  }

  private buildHierarchySubNode(
    ownerLabel: string,
    sourceLabel: string,
    subLabel: string,
    subGroup: GenerationDetailsHierarchySubGroup,
  ): GenerationDetailsHierarchyNode {
    return {
      row: this.buildAggregateRow(
        `subcat-total-${this.slugify(ownerLabel)}-${this.slugify(sourceLabel)}-${this.slugify(subLabel)}`,
        `Total ${subLabel}`,
        subGroup.rows,
        3,
        'subcat-total',
        true,
      ),
      children: subGroup.rows.map((leafRow) => ({ row: leafRow, children: [] })),
    };
  }

  private buildAggregateRow(
    rowKey: string,
    label: string,
    rows: GenerationDetailsRow[],
    rowLevel: number,
    rowKind: 'generation-total' | 'owner-total' | 'source-total' | 'subcat-total',
    hasChildren: boolean,
    options: GenerationDetailsAggregateRowOptions = {},
  ): GenerationDetailsRow {
    const {
      includeTotalInLabel = true,
      includeInstalledCapacityInLabel = false,
      forceExpandChildren = false,
    } = options;
    const blocks = this.buildAggregateBlocks(rows);
    const dayTotal = this.calculateDayTotal(rows);
    const installedCapacity = this.sumInstalledCapacity(rows);
    const rowTheme = this.resolveAggregateRowTheme(rowKind);
    const labelValue = includeInstalledCapacityInLabel ? installedCapacity : Number(dayTotal.toFixed(5));
    const stationName = includeTotalInLabel ? `${label} (${this.formatNumber(labelValue)})` : label;

    return {
      station_id: 0,
      station_name: stationName,
      station_code: null,
      owner: null,
      source_category: null,
      sub_category: null,
      other_parameter: null,
      generation_id: 0,
      generation_name: null,
      generation_type: null,
      blocks,
      day_total_mwh: Number(dayTotal.toFixed(5)),
      installed_capacity: installedCapacity,
      row_key: rowKey,
      row_kind: rowKind,
      row_level: rowLevel,
      has_children: hasChildren,
      force_expand_children: forceExpandChildren,
      editable_station_id: null,
      row_theme: rowTheme,
    };
  }

  private buildAggregateBlocks(rows: GenerationDetailsRow[]): GenerationDetailsBlockCell[] {
    return this.blockColumns.map((block) => ({
      block_no: block.block_no,
      label: block.label,
      period: block.period,
      value: this.sumBlockValues(rows, block.block_no),
      remarks: null,
      source: null,
      editable_id: null,
      editor_role_name: null,
      editor_role_group: null,
    }));
  }

  private buildCalculatedAggregateBlocks(
    positiveRows: GenerationDetailsRow[],
    positiveBonusRows: GenerationDetailsRow[] = [],
    negativeRows: GenerationDetailsRow[] = [],
  ): GenerationDetailsBlockCell[] {
    const combinedPositiveRows = [...positiveRows, ...positiveBonusRows];
    return this.blockColumns.map((block) => ({
      block_no: block.block_no,
      label: block.label,
      period: block.period,
      value: Number((this.sumBlockValues(combinedPositiveRows, block.block_no) - this.sumBlockValues(negativeRows, block.block_no)).toFixed(5)),
      remarks: null,
      source: null,
      editable_id: null,
      editor_role_name: null,
      editor_role_group: null,
    }));
  }

  private buildFooterRows(rows: GenerationDetailsRow[]): GenerationDetailsRow[] {
    return [];
  }

  private buildFooterAggregateRow(rowKey: string, label: string, rows: GenerationDetailsRow[]): GenerationDetailsRow {
    return this.buildAggregateRow(rowKey, label, rows, 0, 'generation-total', false, { includeTotalInLabel: false });
  }

  private buildFormulaFooterRow(
    rowKey: string,
    label: string,
    generationRows: GenerationDetailsRow[],
    importRows: GenerationDetailsRow[],
    exportRows: GenerationDetailsRow[],
  ): GenerationDetailsRow {
    const blocks = this.buildCalculatedAggregateBlocks(generationRows, importRows, exportRows);
    const dayTotal = this.calculateDayTotalFromBlocks(blocks);
    return {
      station_id: 0,
      station_name: label,
      station_code: null,
      owner: null,
      source_category: null,
      sub_category: null,
      other_parameter: null,
      generation_id: 0,
      generation_name: null,
      generation_type: null,
      blocks,
      day_total_mwh: dayTotal,
      row_key: rowKey,
      row_kind: 'generation-total',
      row_level: 0,
      has_children: false,
      editable_station_id: null,
      row_theme: 'slate',
    };
  }

  private buildFooterZeroRow(rowKey: string, label: string): GenerationDetailsRow {
    const blocks = this.blockColumns.map((block) => ({
      block_no: block.block_no,
      label: block.label,
      period: block.period,
      value: 0,
      remarks: null,
      source: null,
      editable_id: null,
      editor_role_name: null,
      editor_role_group: null,
    }));
    return {
      station_id: 0,
      station_name: label,
      station_code: null,
      owner: null,
      source_category: null,
      sub_category: null,
      other_parameter: null,
      generation_id: 0,
      generation_name: null,
      generation_type: null,
      blocks,
      day_total_mwh: 0,
      row_key: rowKey,
      row_kind: 'generation-total',
      row_level: 0,
      has_children: false,
      editable_station_id: null,
      row_theme: 'slate',
    };
  }

  private calculateDayTotalFromBlocks(blocks: GenerationDetailsBlockCell[]): number {
    return Number(blocks.reduce((sum, cell) => sum + (cell.value ?? 0), 0).toFixed(5));
  }

  private sumBlockValues(rows: GenerationDetailsRow[], blockNo: number): number {
    const total = rows.reduce((sum, row) => {
      const cell = row.blocks.find((item) => item.block_no === blockNo);
      return sum + (cell?.value ?? 0);
    }, 0);
    return Number(total.toFixed(5));
  }

  private calculateDayTotal(rows: GenerationDetailsRow[]): number {
    return Number(rows.reduce((sum, row) => sum + row.day_total_mwh, 0).toFixed(5));
  }

  private sumInstalledCapacity(rows: GenerationDetailsRow[]): number {
    return Number(rows.reduce((sum, row) => sum + (row.installed_capacity ?? 0), 0).toFixed(5));
  }

  private resolveAggregateRowTheme(rowKind: 'generation-total' | 'owner-total' | 'source-total' | 'subcat-total'): 'emerald' | 'blue' | 'amber' {
    if (rowKind === 'source-total') {
      return 'blue';
    }
    if (rowKind === 'subcat-total') {
      return 'amber';
    }
    return 'emerald';
  }

  private flattenHierarchyNodes(nodes: GenerationDetailsHierarchyNode[], expandedKeys: Set<string>): GenerationDetailsRow[] {
    const rows: GenerationDetailsRow[] = [];
    for (const node of nodes) {
      rows.push(node.row);
      const shouldForceExpand = this.detailMode === 'summary' && node.row.force_expand_children;
      if (node.row.has_children && node.row.row_key && (shouldForceExpand || expandedKeys.has(node.row.row_key))) {
        rows.push(...this.flattenHierarchyNodes(node.children, expandedKeys));
      }
    }
    return rows;
  }

  private getDefaultExpandedRowKeys(nodes: GenerationDetailsHierarchyNode[]): Set<string> {
    const keys = new Set<string>();

    if (this.detailMode === 'summary') {
      this.collectSummaryExpandedKeys(nodes, keys);
      keys.delete('');
      return keys;
    }

    for (const node of nodes) {
      if (!node.row.has_children || !node.row.row_key) {
        continue;
      }

      keys.add(node.row.row_key);
      for (const child of node.children) {
        if (this.shouldExpandDefaultPowerChild(child)) {
          keys.add(child.row.row_key ?? '');
        }
      }
    }

    keys.delete('');
    return keys;
  }

  private getDefaultFlatExpandedRowKeys(nodes: GenerationDetailsHierarchyNode[]): Set<string> {
    const keys = new Set<string>();
    this.addDefaultFlatExpandedRowKeys(nodes, keys);
    return keys;
  }

  private addDefaultFlatExpandedRowKeys(nodes: GenerationDetailsHierarchyNode[], keys: Set<string>): void {
    for (const node of nodes) {
      if (node.row.has_children && node.row.row_key) {
        keys.add(node.row.row_key);
      }
    }
  }

  private getRowBlockMaxValue(row: GenerationDetailsRow): number | null {
    const values = row.blocks
      .map((cell) => cell.value)
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
    return values.length ? Number(Math.max(...values).toFixed(5)) : null;
  }

  private collectSummaryExpandedKeys(nodes: GenerationDetailsHierarchyNode[], keys: Set<string>): void {
    for (const node of nodes) {
      const hasSubcategoryChildren = node.children.some((child) => child.row.row_kind === 'subcat-total');
      const hasExpandableDescendants = node.children.some(
        (child) => child.row.row_kind === 'owner-total' || child.row.row_kind === 'source-total' || child.row.row_kind === 'generation-total',
      );

      if (
        node.row.row_key &&
        (
          node.row.row_kind === 'generation-total' ||
          (node.row.row_kind === 'owner-total' && hasExpandableDescendants) ||
          (node.row.row_kind === 'source-total' && hasSubcategoryChildren)
        )
      ) {
        keys.add(node.row.row_key);
      }

      if (node.children.length) {
        this.collectSummaryExpandedKeys(node.children, keys);
      }
    }
  }

  private getHierarchySourceChildren(
    hasFuelGroups: boolean,
    subNodes: GenerationDetailsHierarchyNode[],
    directLeafNodes: GenerationDetailsHierarchyNode[],
  ): GenerationDetailsHierarchyNode[] {
    if (this.detailMode === 'summary') {
      return hasFuelGroups ? subNodes : [];
    }

    return hasFuelGroups ? [...subNodes, ...directLeafNodes] : directLeafNodes;
  }

  private shouldExpandSummaryNode(node: GenerationDetailsHierarchyNode): boolean {
    return Boolean(
      node.row.row_kind === 'generation-total' ||
      node.row.row_kind === 'owner-total' ||
      node.row.row_kind === 'source-total',
    );
  }

  private shouldExpandDefaultPowerChild(node: GenerationDetailsHierarchyNode): boolean {
    if (node.row.row_kind !== 'source-total' || !node.row.row_key) {
      return false;
    }

    return node.children.some((child) => child.row.row_kind === 'subcat-total');
  }

  private buildBlockGroups(blocks: GenerationDetailsBlockDefinition[]): GenerationDetailsBlockGroup[] {
    const groups: GenerationDetailsBlockGroup[] = [];
    let currentGroup: GenerationDetailsBlockGroup | null = null;
    for (const block of blocks) {
      if (currentGroup && currentGroup.period === block.period) {
        currentGroup.span += 1;
        continue;
      }
      currentGroup = {
        period: block.period,
        label: this.getPeriodTitle(block.period),
        span: 1,
      };
      groups.push(currentGroup);
    }
    return groups;
  }

  private extractReport(response: unknown): GenerationDetailsReportResponse | null {
    const value = this.extractResponseValue(response);
    if (!value) {
      return null;
    }

    const blocks = this.extractBlockDefinitions(value);
    const rows = this.extractRows(value);
    const summary = this.extractSummary(value);
    const dataDate = this.extractStringValue(value, 'data_date') ?? this.getSelectedDateValue();

    return {
      data_date: dataDate,
      date_label: formatGenerationDetailsDateLabel(dataDate),
      view_mode: this.viewMode,
      show_values: this.normalizeValueOptionValue(this.extractStringValue(value, 'show_values')) ?? this.normalizeSelectedShowValues(),
      value_mode: this.normalizeDisplayValueMode(this.extractStringValue(value, 'value_mode')) ?? this.normalizeSelectedValueMode(),
      detail_mode: this.extractStringValue(value, 'detail_mode'),
      blocks,
      rows,
      summary,
      base_load: this.normalizeNumber(this.getRecordValue(value, 'base_load')) ?? 0,
      restricted_user_mode: Boolean(value['restricted_user_mode']),
      filters: {
        generation_id: null,
        substation_id: this.normalizeNumber(this.filtersForm.get('substation_id')?.value),
        show_values: this.normalizeValueOptionValue(this.extractStringValue(value, 'show_values')) ?? this.normalizeSelectedShowValues(),
        detail_mode: this.extractStringValue(value, 'detail_mode'),
      },
    };
  }

  private extractBlockDefinitions(value: unknown): GenerationDetailsBlockDefinition[] {
    return this.getArrayValue(value, 'blocks').map((item) => {
      const record = this.getRecord(item);
      return {
        block_no: this.normalizeNumber(record['block_no']) ?? 0,
        label: this.extractStringValue(record, 'label') ?? '',
        period: this.extractStringValue(record, 'period') as GenerationDetailsBlockDefinition['period'],
      };
    });
  }

  private extractRows(value: unknown): GenerationDetailsRow[] {
    return this.getArrayValue(value, 'rows').map((item) => this.extractRow(item));
  }

  private extractRow(item: unknown): GenerationDetailsRow {
    const record = this.getRecord(item);
    const blocks = this.extractBlocks(record);
    const stationId = this.normalizeNumber(record['station_id']) ?? 0;

    return {
      station_id: stationId,
      station_name: this.extractStringValue(record, 'station_name') ?? '-',
      station_code: this.extractStringValue(record, 'station_code'),
      owner: this.extractStringValue(record, 'owner'),
      source_category: this.extractStringValue(record, 'source_category'),
      sub_category: this.extractStringValue(record, 'sub_category'),
      other_parameter: this.extractStringValue(record, 'other_parameter'),
      generation_id: this.normalizeNumber(record['generation_id']) ?? 0,
      generation_name: this.extractStringValue(record, 'generation_name'),
      generation_type: this.extractStringValue(record, 'generation_type'),
      blocks,
      day_total_mwh: this.normalizeNullableNumber(record['day_total_mwh']) ?? this.calculateRowTotal(blocks),
      installed_capacity: this.normalizeNullableNumber(record['installed_capacity']),
      row_key: this.extractStringValue(record, 'row_key') ?? `data-${stationId}`,
      row_kind: this.extractStringValue(record, 'row_kind') ?? 'data',
      row_level: this.normalizeNullableNumber(record['row_level']) ?? 3,
      has_children: Boolean(record['has_children']),
      force_expand_children: Boolean(record['force_expand_children']),
      force_flat: Boolean(record['force_flat']),
      parent_row_key: this.extractStringValue(record, 'parent_row_key'),
      is_small_power: Boolean(record['is_small_power']),
      editable_station_id: this.normalizeNumber(record['editable_station_id']) ?? stationId,
      row_theme: this.extractStringValue(record, 'row_theme') ?? 'slate',
    };
  }

  private extractBlocks(record: Record<string, unknown>): GenerationDetailsBlockCell[] {
    return this.getArrayValue(record, 'blocks').map((blockItem) => {
      const blockRecord = this.getRecord(blockItem);
      return {
        block_no: this.normalizeNumber(blockRecord['block_no']) ?? 0,
        label: this.extractStringValue(blockRecord, 'label') ?? '',
        period: this.extractStringValue(blockRecord, 'period') as GenerationDetailsBlockCell['period'],
        value: this.normalizeNullableNumber(blockRecord['value']),
        remarks: this.extractStringValue(blockRecord, 'remarks'),
        source: this.extractStringValue(blockRecord, 'source') as GenerationDetailsBlockCell['source'],
        editable_id: this.normalizeNullableNumber(blockRecord['editable_id']),
        editor_role_name: this.extractStringValue(blockRecord, 'editor_role_name'),
        editor_role_group: this.extractStringValue(blockRecord, 'editor_role_group') as GenerationDetailsBlockCell['editor_role_group'],
      };
    });
  }

  private calculateRowTotal(blocks: GenerationDetailsBlockCell[]): number {
    return Number(blocks.reduce((sum, cell) => sum + (cell.value ?? 0), 0).toFixed(5));
  }

  private extractSummary(value: unknown): GenerationDetailsSummary {
    const summaryRecord = this.getRecordValue(value, 'summary');
    const record = this.getRecord(summaryRecord);
    return {
      total_mwh: this.normalizeNumber(record['total_mwh']) ?? 0,
      row_count: this.normalizeNumber(record['row_count']) ?? 0,
    };
  }

  private extractSubstationOptions(response: unknown): GenerationDetailsSubstationOption[] {
    const value = this.extractResponseValue(response);
    return this.getArrayValue(value, 'generators').map((item) => {
      const record = this.getRecord(item);
      return {
        id: this.normalizeNumber(record['id']) ?? 0,
        name: this.extractStringValue(record, 'generator_name') ?? '-',
        station_code: this.extractStringValue(record, 'scada_station_code'),
        other_parameter: this.extractStringValue(record, 'other_parameter'),
      };
    });
  }

  private extractPreviousBlockValue(response: unknown): { data_value: number | null; remarks: string | null; previous_block_no: number | null; source: string | null } | null {
    const value = this.extractResponseValue(response);
    if (!value) {
      return null;
    }

    return {
      data_value: this.normalizeNullableNumber(value['data_value']),
      remarks: this.extractStringValue(value, 'remarks'),
      previous_block_no: this.normalizeNullableNumber(value['previous_block_no']),
      source: this.extractStringValue(value, 'source'),
    };
  }

  private filterSubstationAutocomplete(query: string): void {
    const normalizedQuery = typeof query === 'string' ? query.trim().toLowerCase() : '';
    const visibleOptions = this.getVisibleSubstationOptions();
    if (!normalizedQuery) {
      this.filteredSubstationOptions = [...visibleOptions];
      return;
    }

    this.filteredSubstationOptions = visibleOptions.filter((option) => {
      const name = (option.name ?? '').toLowerCase();
      const code = (option.station_code ?? '').toLowerCase();
      return name.includes(normalizedQuery) || code.includes(normalizedQuery);
    });
  }

  private commitSubstationAutocompleteValue(): void {
    const currentValue = typeof this.substationSearchTerm === 'string' ? this.substationSearchTerm.trim() : '';
    if (!currentValue) {
      this.resetSubstationSelection(true);
      return;
    }

    const match = this.findSubstationByLabel(currentValue);
    if (!match) {
      this.resetSubstationSelection(true);
      return;
    }

    this.substationSearchTerm = match.name;
    this.setSubstationSelection(match.id, true);
  }

  private resetSubstationSelection(emitEvent: boolean): void {
    const selectedId = this.normalizeNumber(this.filtersForm.get('substation_id')?.value);
    if (selectedId !== null) {
      this.filtersForm.patchValue({ substation_id: null }, { emitEvent });
    }
  }

  private setSubstationSelection(substationId: number, emitEvent: boolean): void {
    const selectedId = this.normalizeNumber(this.filtersForm.get('substation_id')?.value);
    if (selectedId !== substationId) {
      this.filtersForm.patchValue({ substation_id: substationId }, { emitEvent });
    }
  }

  private findSubstationByLabel(label: string): GenerationDetailsSubstationOption | null {
    const normalizedLabel = label.trim().toLowerCase();
    if (!normalizedLabel) {
      return null;
    }

    return this.getVisibleSubstationOptions().find((option) => (option.name ?? '').trim().toLowerCase() === normalizedLabel) ?? null;
  }

  private syncSubstationAutocompleteValue(substationId: number | null | undefined): void {
    const match = this.getVisibleSubstationOptions().find((option) => option.id === substationId);
    if (!match) {
      if (substationId === null || substationId === undefined) {
        this.substationSearchTerm = '';
      }
      return;
    }

    this.substationSearchTerm = match.name;
  }

  private normalizeSelectedValueMode(): 'MW' | 'MWH' {
    const value = this.filtersForm.get('show_values')?.value;
    return this.normalizeDisplayValueMode(typeof value === 'string' ? value : null) ?? 'MWH';
  }

  private normalizeSelectedShowValues(): GenerationDetailsValueFilterOption | null {
    const value = this.filtersForm.get('show_values')?.value;
    return this.normalizeValueOptionValue(typeof value === 'string' ? value : null);
  }

  private normalizeValueOptionValue(value: string | null | undefined): GenerationDetailsValueFilterOption | null {
    if (typeof value !== 'string') {
      return null;
    }

    const normalizedValue = value.trim().toUpperCase();
    if (normalizedValue === 'ENERGYMETER') {
      return 'ENERGY_METER';
    }

    return this.valueOptions.some((option) => option.value === normalizedValue) || normalizedValue === 'MW' || normalizedValue === 'MWH'
      ? (normalizedValue as GenerationDetailsValueFilterOption)
      : null;
  }

  private normalizeDisplayValueMode(value: string | null | undefined): 'MW' | 'MWH' | null {
    const normalizedValue = typeof value === 'string' ? value.trim().toUpperCase() : '';
    if (normalizedValue === 'MW' || normalizedValue === 'MWH') {
      return normalizedValue;
    }

    return null;
  }

  private refreshSubstationOptionsForDetailMode(): void {
    if (!this.showSubstationFilter) {
      this.filteredSubstationOptions = [];
      this.substationSearchTerm = '';
      return;
    }

    const visibleOptions = this.getVisibleSubstationOptions();
    this.filteredSubstationOptions = [...visibleOptions];

    const selectedSubstationId = this.normalizeNumber(this.filtersForm.get('substation_id')?.value);
    if (selectedSubstationId === null) {
      this.substationSearchTerm = '';
      return;
    }

    const match = visibleOptions.find((option) => option.id === selectedSubstationId);
    if (match) {
      this.substationSearchTerm = match.name;
      return;
    }

    this.filtersForm.patchValue({ substation_id: null }, { emitEvent: false });
    this.substationSearchTerm = '';
  }

  private getVisibleSubstationOptions(): GenerationDetailsSubstationOption[] {
    if (!this.showSubstationFilter) {
      return [];
    }

    if (!this.isWaterLevelDetailMode()) {
      return this.substationOptions;
    }

    return this.substationOptions.filter((option) => this.hasWaterLevelParameter(option.other_parameter));
  }

  private hasWaterLevelParameter(value: string | null | undefined): boolean {
    const normalizedValue = typeof value === 'string' ? value.trim().toLowerCase().replace(/[\s_-]+/g, '') : '';
    return normalizedValue.includes('waterlevel');
  }

  private isWaterLevelDetailMode(): boolean {
    return this.detailMode === 'water-level';
  }

  private isDirectRowsDetailMode(): boolean {
    return this.detailMode === 'spill' || this.detailMode === 'voltage' || this.detailMode === 'interruption' || this.detailMode === 'water-level';
  }

  private getSelectedDateValue(): string {
    const value = this.getDateControlValue();
    return value ? this.toApiDateValue(value) : getTodayDateValue();
  }

  private createCellKey(stationId: number, blockNo: number): string {
    return [stationId, blockNo].join('-');
  }

  private createDayTotalKey(stationId: number): string {
    return ['day', stationId].join('-');
  }

  private resetCellEditor(): void {
    this.activeCellKey = null;
    this.cellDraftValue = '';
  }

  private resetRemarksForm(): void {
    this.remarksForm.reset({ remarks: '' });
  }

  private buildExportFileName(): string {
    return `generation_details_report_${this.getSelectedDateValue()}.xlsx`;
  }

  private buildTemplateFileName(mode: GenerationDetailsExcelMode): string {
    return `generation_details_${mode}_template_${this.getSelectedDateValue()}.xlsx`;
  }

  private resetImportInput(input: HTMLInputElement | null): void {
    if (input) {
      input.value = '';
    }
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  }

  private normalizeHierarchyLabel(value: string | null | undefined): string {
    const candidate = typeof value === 'string' ? value.trim() : '';
    if (candidate.toUpperCase() === 'NEA SUBSIDIARY') {
      return 'NEA Subsidiary';
    }
    return candidate || 'Unassigned';
  }

  private normalizeOptionalHierarchyLabel(value: string | null | undefined): string | null {
    const candidate = typeof value === 'string' ? value.trim() : '';
    return candidate && !this.isMissingHierarchyLabel(candidate) ? candidate : null;
  }

  private isMissingHierarchyLabel(value: string | null | undefined): boolean {
    const candidate = typeof value === 'string' ? value.trim().toLowerCase() : '';
    return candidate === '' || candidate === 'na' || candidate === 'n/a' || candidate === 'null' || candidate === '-';
  }

  private isImportParameter(value: string | null | undefined): boolean {
    const candidate = typeof value === 'string' ? value.trim().toLowerCase() : '';
    if (!candidate) {
      return false;
    }

    return candidate.includes('+') || candidate.includes('import');
  }

  private normalizeGenerationRows(rows: GenerationDetailsRow[]): GenerationDetailsRow[] {
    return rows.map((row) => ({
      ...row,
      row_kind: row.row_kind === 'power-small' ? row.row_kind : 'data',
      row_level: row.row_kind === 'power-small' ? 1 : 4,
      row_key: row.row_kind === 'power-small' ? row.row_key : `data-${row.station_id}`,
      has_children: false,
      editable_station_id: row.row_kind === 'power-small' ? null : row.station_id,
    }));
  }

  private slugify(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  }

  private isRestrictedGenerationUserRole(roleName: string | null | undefined): boolean {
    const normalizedRoleName = typeof roleName === 'string' ? roleName.trim().toUpperCase() : '';
    return normalizedRoleName === 'GEN_USER' || normalizedRoleName === 'SUBSTATION_USER' || normalizedRoleName === 'SUB_USER';
  }
}
